// client/src/components/stats/leakRanges.ts
//
// Rangos "saludables" (opt: min-max) por stat y por stack bucket.
// Extraídos de StatsView para poder testearlos y reutilizarlos.

export type LeakStatus = 'ok' | 'warn' | 'critical';

export interface LeakRange {
  min: number;
  max: number;
  label: string;
}

export type StackKey = 'all' | 'short' | 'mid' | 'deep';

export const RANGES_BY_STACK: Record<StackKey, Record<string, LeakRange>> = {
  short: {
    // < 25BB
    vpip: { min: 16, max: 22, label: 'VPIP' },
    pfr: { min: 14, max: 19, label: 'PFR' },
    threebet: { min: 10, max: 16, label: '3Bet' },
    squeeze:  { min: 4,  max: 12, label: 'Squeeze' },
    fourbet:  { min: 3,  max: 20, label: '4Bet' },
    openLimp: { min: 0,  max: 3,  label: 'Open Limp' },
    foldTo3bet: { min: 40, max: 55, label: 'Fold to 3Bet' },
    foldTo3betAllin: { min: 30, max: 45, label: 'Fold to 3Bet (vs Shove)' },
    foldTo3betNonAllin: { min: 50, max: 65, label: 'Fold to 3Bet (vs Raise)' },
    ats: { min: 25, max: 35, label: 'ATS' },
    af: { min: 3.0, max: 5.0, label: 'AF' },
    cbetFlop: { min: 35, max: 50, label: 'CBet Flop' },
    cbetFlopSuccess: { min: 40, max: 50, label: 'CBet Flop Success' },
    cbetTurn: { min: 30, max: 45, label: 'Double Barrel' },
    cbetRiver: { min: 20, max: 40, label: 'Triple Barrel' },
    delayCbet: { min: 35, max: 50, label: 'Delayed CBet' },
    cbet3BetPotIP: { min: 60, max: 78, label: 'CBet 3bet Pot IP' },
    cbet3BetPotOOP: { min: 45, max: 62, label: 'CBet 3bet Pot OOP' },
    foldToCbetFlop: { min: 30, max: 45, label: 'Fold to CBet Flop' },
    foldToCbetTurn: { min: 25, max: 40, label: 'Fold to Double Barrel' },
    callCbetTurn: { min: 45, max: 60, label: 'Call Double Barrel' },
    donkFlop: { min: 0, max: 5, label: 'Donk Flop' },
    donkTurn: { min: 0, max: 6, label: 'Donk Turn' },
    donkRiver: { min: 0, max: 7, label: 'Donk River' },
    probeTotal: { min: 15, max: 25, label: 'Probe Bet Total' },
    xraiseFlop: { min: 8, max: 16, label: 'XRaise Flop' },
    wtsd: { min: 35, max: 45, label: 'WTSD' },
    wsd: { min: 50, max: 56, label: 'W@SD' },
    wwsf: { min: 40, max: 48, label: 'WWSF' },
  },
  mid: {
    // 25-50BB
    vpip: { min: 20, max: 26, label: 'VPIP' },
    pfr: { min: 16, max: 21, label: 'PFR' },
    threebet: { min: 8, max: 13, label: '3Bet' },
    squeeze:  { min: 4, max: 11, label: 'Squeeze' },
    fourbet:  { min: 3,  max: 20, label: '4Bet' },
    openLimp: { min: 0, max: 2,  label: 'Open Limp' },
    foldTo3bet: { min: 50, max: 62, label: 'Fold to 3Bet' },
    foldTo3betAllin: { min: 45, max: 60, label: 'Fold to 3Bet (vs Shove)' },
    foldTo3betNonAllin: { min: 55, max: 68, label: 'Fold to 3Bet (vs Raise)' },
    ats: { min: 30, max: 40, label: 'ATS' },
    af: { min: 2.2, max: 3.5, label: 'AF' },
    cbetFlop: { min: 45, max: 65, label: 'CBet Flop' },
    cbetFlopSuccess: { min: 45, max: 55, label: 'CBet Flop Success' },
    cbetTurn: { min: 45, max: 55, label: 'Double Barrel' },
    cbetRiver: { min: 35, max: 50, label: 'Triple Barrel' },
    delayCbet: { min: 40, max: 55, label: 'Delayed CBet' },
    cbet3BetPotIP: { min: 65, max: 80, label: 'CBet 3bet Pot IP' },
    cbet3BetPotOOP: { min: 50, max: 65, label: 'CBet 3bet Pot OOP' },
    foldToCbetFlop: { min: 35, max: 50, label: 'Fold to CBet Flop' },
    foldToCbetTurn: { min: 35, max: 50, label: 'Fold to Double Barrel' },
    callCbetTurn: { min: 40, max: 50, label: 'Call Double Barrel' },
    donkFlop: { min: 0, max: 4, label: 'Donk Flop' },
    donkTurn: { min: 0, max: 5, label: 'Donk Turn' },
    donkRiver: { min: 0, max: 6, label: 'Donk River' },
    probeTotal: { min: 20, max: 30, label: 'Probe Bet Total' },
    xraiseFlop: { min: 8, max: 16, label: 'XRaise Flop' },
    wtsd: { min: 28, max: 35, label: 'WTSD' },
    wsd: { min: 52, max: 58, label: 'W@SD' },
    wwsf: { min: 45, max: 52, label: 'WWSF' },
  },
  deep: {
    // > 50BB
    vpip: { min: 24, max: 30, label: 'VPIP' },
    pfr: { min: 18, max: 24, label: 'PFR' },
    threebet: { min: 8, max: 12, label: '3Bet' },
    squeeze:  { min: 4, max: 10, label: 'Squeeze' },
    fourbet:  { min: 3,  max: 20, label: '4Bet' },
    openLimp: { min: 0, max: 2,  label: 'Open Limp' },
    foldTo3bet: { min: 55, max: 65, label: 'Fold to 3Bet' },
    foldTo3betAllin: { min: 50, max: 65, label: 'Fold to 3Bet (vs Shove)' },
    foldTo3betNonAllin: { min: 55, max: 68, label: 'Fold to 3Bet (vs Raise)' },
    ats: { min: 35, max: 45, label: 'ATS' },
    af: { min: 2.0, max: 3.0, label: 'AF' },
    cbetFlop: { min: 55, max: 70, label: 'CBet Flop' },
    cbetFlopSuccess: { min: 45, max: 55, label: 'CBet Flop Success' },
    cbetTurn: { min: 45, max: 55, label: 'Double Barrel' },
    cbetRiver: { min: 40, max: 55, label: 'Triple Barrel' },
    delayCbet: { min: 42, max: 58, label: 'Delayed CBet' },
    cbet3BetPotIP: { min: 65, max: 80, label: 'CBet 3bet Pot IP' },
    cbet3BetPotOOP: { min: 50, max: 65, label: 'CBet 3bet Pot OOP' },
    foldToCbetFlop: { min: 40, max: 55, label: 'Fold to CBet Flop' },
    foldToCbetTurn: { min: 38, max: 52, label: 'Fold to Double Barrel' },
    callCbetTurn: { min: 38, max: 48, label: 'Call Double Barrel' },
    donkFlop: { min: 0, max: 3, label: 'Donk Flop' },
    donkTurn: { min: 0, max: 4, label: 'Donk Turn' },
    donkRiver: { min: 0, max: 5, label: 'Donk River' },
    probeTotal: { min: 22, max: 33, label: 'Probe Bet Total' },
    xraiseFlop: { min: 8, max: 16, label: 'XRaise Flop' },
    wtsd: { min: 25, max: 32, label: 'WTSD' },
    wsd: { min: 53, max: 60, label: 'W@SD' },
    wwsf: { min: 48, max: 54, label: 'WWSF' },
  },
  all: {
    vpip: { min: 20, max: 27, label: 'VPIP' },
    pfr: { min: 15, max: 19, label: 'PFR' },
    threebet: { min: 8, max: 12, label: '3Bet' },
    squeeze:  { min: 4, max: 11, label: 'Squeeze' },
    fourbet:  { min: 3,  max: 20, label: '4Bet' },
    openLimp: { min: 0, max: 2,  label: 'Open Limp' },
    foldTo3bet: { min: 55, max: 65, label: 'Fold to 3Bet' },
    foldTo3betAllin: { min: 45, max: 60, label: 'Fold to 3Bet (vs Shove)' },
    foldTo3betNonAllin: { min: 55, max: 68, label: 'Fold to 3Bet (vs Raise)' },
    ats: { min: 30, max: 40, label: 'ATS' },
    af: { min: 2.0, max: 3.0, label: 'AF' },
    cbetFlop: { min: 55, max: 70, label: 'CBet Flop' },
    cbetFlopSuccess: { min: 45, max: 55, label: 'CBet Flop Success' },
    cbetTurn: { min: 45, max: 55, label: 'Double Barrel' },
    cbetRiver: { min: 40, max: 50, label: 'Triple Barrel' },
    delayCbet: { min: 40, max: 55, label: 'Delayed CBet' },
    cbet3BetPotIP: { min: 65, max: 80, label: 'CBet 3bet Pot IP' },
    cbet3BetPotOOP: { min: 50, max: 65, label: 'CBet 3bet Pot OOP' },
    foldToCbetFlop: { min: 35, max: 50, label: 'Fold to CBet Flop' },
    foldToCbetTurn: { min: 35, max: 50, label: 'Fold to Double Barrel' },
    callCbetTurn: { min: 40, max: 50, label: 'Call Double Barrel' },
    donkFlop: { min: 0, max: 3, label: 'Donk Flop' },
    donkTurn: { min: 0, max: 4, label: 'Donk Turn' },
    donkRiver: { min: 0, max: 5, label: 'Donk River' },
    probeTotal: { min: 20, max: 30, label: 'Probe Bet Total' },
    xraiseFlop: { min: 8, max: 16, label: 'XRaise Flop' },
    wtsd: { min: 30, max: 35, label: 'WTSD' },
    wsd: { min: 53, max: 58, label: 'W@SD' },
    wwsf: { min: 45, max: 52, label: 'WWSF' },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Evaluate a stat value against its healthy range.
// Returns 'ok' | 'warn' | 'critical' based on how far from the range.
// ─────────────────────────────────────────────────────────────────────────────
export function evalStat(key: string, value: number, stackSize: StackKey): LeakStatus {
  const currentRanges = RANGES_BY_STACK[stackSize] || RANGES_BY_STACK.all;
  const r = currentRanges[key];
  if (!r) return 'ok';
  if (value >= r.min && value <= r.max) return 'ok';
  const gap = value > r.max ? value - r.max : r.min - value;
  const range = r.max - r.min || 5;
  return gap > range * 1.5 ? 'critical' : 'warn';
}

// Helper: get the range for a specific stat (or null)
export function getRange(key: string, stackSize: StackKey): LeakRange | null {
  const currentRanges = RANGES_BY_STACK[stackSize] || RANGES_BY_STACK.all;
  return currentRanges[key] || null;
}
